import { create } from "zustand";
import { trackChunkTypeFrequency } from "../../utils/ChatEventReducer";
import { DefaultChatTransport } from "./conversation/transport/DefaultChatTransport";
import config from "../../config";
import { log as _log, critical as _crit } from "../../utils/ChatStreamLogger";
import { fetchWithIdTokenRetry } from "../../utils/AuthHeaders";

// Transport-only stream store. It owns the SSE connection lifecycle and
// connection-state bookkeeping, and forwards raw events to the caller via
// `onEvent`. It does NOT build or own chat messages — useAgentStore consumes
// ui_chunk events and reduces them into sessionDetail.messages, the single
// source of truth for both live and persisted turns.

let _abortController = null;
let _subscribedSessionId = null;
let _activeTransport = null;
let _typedChunkCounters = {};
let _pollIntervalId = null;
let _visibilityHandler = null;

/** Single AbortController for the client-scoped activity SSE stream (one per tab). */
let _clientActivityAbortController = null;

// Backoff between activity-SSE (re)connections, applied on BOTH a normal stream close
// and an error so a backend that ends the stream immediately can't spin the reconnect
// while-loop into a tight fetch loop.
const SSE_RECONNECT_DELAY_MS = 3000;

function _clearPolling() {
  if (_pollIntervalId) { clearInterval(_pollIntervalId); _pollIntervalId = null; }
  if (_visibilityHandler) { document.removeEventListener('visibilitychange', _visibilityHandler); _visibilityHandler = null; }
}

function _normalizeSessionKey(chatSessionId) {
  return String(chatSessionId || "").trim();
}

const useChatStreamStore = create((set, get) => ({
  sessionStreamStateBySessionId: {},
  streamError: null,

  _setSessionStreamState: (chatSessionId, nextState) => {
    const normalizedSessionId = _normalizeSessionKey(chatSessionId);
    if (!normalizedSessionId) return;
    const normalizedState = String(nextState || "").trim() || "idle";
    set((s) => {
      const cur =
        s.sessionStreamStateBySessionId[normalizedSessionId] || "idle";
      if (cur === normalizedState) return {};
      const level = normalizedState === "disconnected" ? "error"
        : normalizedState === "reconnecting" ? "warn"
        : "info";
      _crit("StreamStore", level, `session=${normalizedSessionId} connState ${cur} → ${normalizedState}`);
      return {
        sessionStreamStateBySessionId: {
          ...s.sessionStreamStateBySessionId,
          [normalizedSessionId]: normalizedState,
        },
      };
    });
  },

  disconnectStream: () => {
    const subscribedSessionId = _subscribedSessionId;
    if (subscribedSessionId) {
      _crit("StreamStore", "info", `disconnectStream session=${subscribedSessionId}`);
    }
    _clearPolling();
    if (_activeTransport) {
      _activeTransport.disconnect();
      _activeTransport = null;
    }
    if (_abortController) {
      _abortController.abort();
      _abortController = null;
    }
    if (subscribedSessionId) {
      get()._setSessionStreamState(subscribedSessionId, "idle");
    }
    _subscribedSessionId = null;
  },

  /**
   * Subscribe to the single client-scoped activity SSE stream. One connection carries
   * new_session/session_activity for EVERY agent owned by the tenant (fanned in server-side
   * on the client:{clientId} channel), replacing the previous one-connection-per-agent model
   * so connections scale with open tabs, not with agents on screen. The target agent is read
   * from `data.payload.agentId` and forwarded via onActivity(agentId, eventType, wrapper) — the
   * same contract handleAgentActivityEvent already consumes. Call disconnectClientActivity to
   * tear down.
   */
  subscribeToClientActivity: (onActivity) => {
    get().disconnectClientActivity();
    const abortController = new AbortController();
    _clientActivityAbortController = abortController;

    (async () => {
      while (!abortController.signal.aborted) {
        try {
          const url = `${config.AI_SERVICE_BASE}/ai/agent/activity/stream`;
          const response = await fetchWithIdTokenRetry(url, {
            signal: abortController.signal,
          });
          if (!response.ok) throw new Error(`SSE failed: ${response.status}`);

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          let eventType = "";
          let dataStr = "";

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop();
            for (const line of lines) {
              if (line.startsWith("event:")) {
                eventType = line.slice(6).trim();
              } else if (line.startsWith("data:")) {
                dataStr = line.slice(5).trim();
              } else if (line === "" && eventType) {
                if (eventType === "new_session" || eventType === "session_activity") {
                  let data = null;
                  try { data = JSON.parse(dataStr); } catch (_) {}
                  const agentId = data?.payload?.agentId;
                  if (agentId && onActivity) onActivity(agentId, eventType, data);
                }
                eventType = "";
                dataStr = "";
              }
            }
          }
        } catch (e) {
          if (abortController.signal.aborted) return;
          _crit("StreamStore", "warn", `clientActivity SSE error, retrying: ${e?.message || e}`);
        }
        if (abortController.signal.aborted) return;
        await new Promise((r) => setTimeout(r, SSE_RECONNECT_DELAY_MS));
      }
    })();
  },

  disconnectClientActivity: () => {
    if (_clientActivityAbortController) {
      _clientActivityAbortController.abort();
      _clientActivityAbortController = null;
    }
  },

  /**
   * Subscribe to session SSE events.
   * @param {string} connectId — chatSessionId for private, publicSessionToken for public
   * @param {string} chatSessionId — always the real chatSessionId (used as state key)
   * @param {object} options — { reconnectWhileSubscribed, onEvent, shouldReconnect, skipAuth }
   */
  subscribeToSession: async (connectId, chatSessionId, options = {}) => {
    // Coalesce concurrent calls. _abortController is set as soon as a subscribe
    // begins (well before _activeTransport is wired post-await), so checking only
    // _activeTransport lets a second call slip through during the auth-token await
    // and tear down the in-flight transport.
    if (_subscribedSessionId === chatSessionId && (_activeTransport || _abortController)) {
      const forceReconnect = options?.forceReconnect === true;
      if (!forceReconnect) {
        _log("StreamStore", `subscribeToSession: already subscribed to ${chatSessionId}, skipping`);
        return { aborted: false, connectionFailed: false };
      }
      _crit("StreamStore", "warn", `subscribeToSession: forceReconnect ${chatSessionId} — tearing down old transport`);
      get().disconnectStream();
    }

    const isSessionTerminal = options?.isSessionTerminal;
    if (typeof isSessionTerminal === 'function' && isSessionTerminal()) {
      return { aborted: false, connectionFailed: false };
    }

    get().disconnectStream();
    const reconnectWhileSubscribed = options?.reconnectWhileSubscribed === true;
    const onEvent = options?.onEvent;
    const shouldReconnectFn = options?.shouldReconnect;
    const skipAuth = options?.skipAuth === true;
    const onPollFallback = options?.onPollFallback;
    const pollIntervalMs = options?.pollIntervalMs || 3000;
    const isSessionActive = options?.isSessionActive;

    const abortController = new AbortController();
    _subscribedSessionId = chatSessionId;
    _abortController = abortController;
    _crit("StreamStore", "info", `subscribeToSession session=${chatSessionId} public=${skipAuth} reconnectWhileSubscribed=${reconnectWhileSubscribed}`);
    get()._setSessionStreamState(chatSessionId, "connecting");
    set({ streamError: null });

    // Start polling fallback + visibility recovery
    _clearPolling();
    if (typeof onPollFallback === 'function') {
      _pollIntervalId = setInterval(() => {
        const active = typeof isSessionActive === 'function' ? isSessionActive() : true;
        if (!active) { _clearPolling(); return; }
        const state = get().sessionStreamStateBySessionId[chatSessionId] || 'idle';
        if (state !== 'connected' || options.shouldPollConnected?.()) {
          onPollFallback(chatSessionId);
        }
      }, pollIntervalMs);

      _visibilityHandler = () => {
        if (document.visibilityState === 'visible') {
          const active = typeof isSessionActive === 'function' ? isSessionActive() : true;
          const state = get().sessionStreamStateBySessionId[chatSessionId] || 'idle';
          if (active && state !== 'connected') onPollFallback(chatSessionId);
        }
      };
      document.addEventListener('visibilitychange', _visibilityHandler);
    }

    const transport = new DefaultChatTransport({
      baseUrl: config.AI_SERVICE_BASE,
      skipAuth,
      onEvent: (eventName, data, seq) => {
        if (_abortController === abortController && onEvent) {
          return onEvent(eventName, data, chatSessionId, seq);
        }
      },
      onConnectionStateChange: (nextState) => {
        if (_abortController !== abortController) return;
        get()._setSessionStreamState(chatSessionId, nextState);
      },
      onStreamDiagnostic: (info) => {
        trackChunkTypeFrequency(`diag:${info.event}`, _typedChunkCounters);
      },
    });
    _activeTransport = transport;
    const result = await transport.connect(connectId, {
      isSessionTerminal,
      shouldReconnect: () => {
        const should = typeof shouldReconnectFn === "function" ? shouldReconnectFn() : true;
        if (!should) return false;
        if (reconnectWhileSubscribed) return true;
        return false;
      },
      maxRetries: 5,
    });
    // An old connection can settle after its replacement has installed new
    // polling and listeners. Only the owner may tear those resources down.
    if (_abortController !== abortController) return { aborted: true };
    if (!result.connectionFailed) {
      _clearPolling();
    }

    if (result.aborted) { _clearPolling(); return { aborted: true }; }

    if (_abortController === abortController) {
      _abortController = null;
    }
    if (result.connectionFailed && _subscribedSessionId === chatSessionId) {
      _crit("StreamStore", "error", `subscribeToSession session=${chatSessionId} connectionFailed — stream lost`);
      get()._setSessionStreamState(chatSessionId, "disconnected");
      set({ streamError: "Connection lost. Messages may be delayed." });
    }

    if (_activeTransport === transport) {
      _activeTransport = null;
    }
    if (_subscribedSessionId === chatSessionId && !result.connectionFailed) {
      _subscribedSessionId = null;
    }

    return result;
  },

  getSubscribedSessionId: () => _subscribedSessionId,
}));

export default useChatStreamStore;
